'use client';

import { useEffect, useRef, useState } from 'react';

import { useSetAtom } from 'jotai';

import { useElementObserver } from '@core/Hooks/UseElementObserver';
import { useIntersectionObserver } from '@core/Hooks/UseIntersectionObserver';
import { Typography } from '@gds/Typography/Typography';
import { dealerNumberErrorAtom } from '@pdp/Atoms/DealerNumberError.atom';
import { showDealerNumberPdpAtom } from '@pdp/Atoms/ShowDealerNumberPdp.atom';
import { LeadCTAs } from '@pdp/Components/LeadCTAs/LeadCTAs';
import { VehicleAttributes } from '@pdp/Components/VehicleAttributes/VehicleAttributes';
import { adminFeeMessagesAtom } from '@shared-components/AdminFee/AdminFee.atoms';

import { GuaranteeWrapper } from '../GuaranteeWrapper/GuaranteeWrapper';
import { SavedSearchCta } from '../SavedSearchCta/SavedSearchCta';
import type { StickyInfoProps } from './StickyInfo.entity';

import styles from './StickyInfo.module.css';

export const StickyInfo = ({
  strings: {
    pdpStrings,
    vehicleAttributesStrings,
    leadFormMessages,
    guaranteeStrings,
    priceRatingStrings,
    savedSearchMessages,
    adminFeeStrings,
    testDriveCtaStrs,
  },
  vehicle,
  hasCampaignDiscount,
  isStockUnavailable,
}: StickyInfoProps) => {
  const $stickyInfo = useRef<HTMLDivElement>(null);
  const $sentinel = useRef<HTMLDivElement>(null);
  const hasStartedAutoGenerateNo = useRef(false);
  const [isSticky, setIsSticky] = useState(false);

  const setIsNumberVisible = useSetAtom(showDealerNumberPdpAtom);
  const setDealerNumberError = useSetAtom(dealerNumberErrorAtom);
  const setAdminFeeStrings = useSetAtom(adminFeeMessagesAtom);

  useEffect(() => {
    setIsNumberVisible(false);
    setDealerNumberError(false);
    setAdminFeeStrings(adminFeeStrings);
  }, []);

  // Observer for sticky info height
  useElementObserver($stickyInfo, entry => {
    const height = entry.contentBoxSize[0].blockSize;
    const root = document.querySelector(':root') as HTMLElement;

    if (window.scrollY > 80 && window.innerWidth > 1023) {
      root.style.setProperty('--sticky-info-height', `${height}px`);
    } else {
      root.style.removeProperty('--sticky-info-height');
    }
  });

  useIntersectionObserver($sentinel, entry => setIsSticky(!entry.isIntersecting), {
    rootMargin: '-72px 0px 0px 0px',
  });

  return (
    <>
      <div ref={$sentinel} className={styles.sentinel} />
      <div
        ref={$stickyInfo}
        className={styles.stickyInfo}
        id="sticky-info"
        data-test-id="stickyInfo"
        data-stuck={isSticky}
      >
        <VehicleAttributes
          strings={vehicleAttributesStrings}
          vehicle={vehicle}
          hasCampaignDiscount={hasCampaignDiscount}
          priceRatingStrings={priceRatingStrings}
          isStockUnavailable={isStockUnavailable}
        />
        {vehicle.spec?.guarantee && (
          <GuaranteeWrapper messages={guaranteeStrings} guarantee={vehicle.spec.guarantee} />
        )}

        {!isStockUnavailable ? (
          <LeadCTAs
            vehicle={vehicle}
            strings={{ pdpStrings, leadFormMessages, testDriveCtaStrs }}
            isEcom={!!vehicle.ecom.isEcom}
            hasInactiveStatus={vehicle.details.status === 'inactive'}
            isSticky={isSticky}
            hasStartedAutoGenerateNo={hasStartedAutoGenerateNo}
          />
        ) : (
          <>
            <Typography className={styles.stockUnavailable} variant={'subheading4'}>
              {pdpStrings.unavailableVehicleLabel}
            </Typography>
            <SavedSearchCta
              strings={savedSearchMessages}
              make={vehicle.make.id}
              model={vehicle.model.id}
            />
          </>
        )}
      </div>
    </>
  );
};

StickyInfo.displayName = 'StickyInfo';
